import { useEffect, useMemo, useRef, useState } from 'react';
import { isContainer, type NodeData, type NodeId } from '@vellum/core';
import { app } from '../app.ts';
import { useApp } from '../hooks.ts';
import { Icons, nodeIcon } from './icons.tsx';
import { AssetsPanel } from './AssetsPanel.tsx';

const ROW = 28;

interface Row {
  node: NodeData;
  depth: number;
  hasKids: boolean;
}

const expanded = new Set<NodeId>();

export function LayersPanel() {
  const pageId = useApp(['doc', 'selection'], () => app.editor.pageId);
  const pages = useApp('doc', () => app.doc.pages());
  const [, setTick] = useState(0);
  const [tab, setTab] = useState<'layers' | 'assets'>('layers');
  return (
    <div className="panel" onPointerDown={(e) => e.stopPropagation()}>
      <div className="panel-section">
        <div className="panel-title">
          <span>Pages</span>
          <button title="Add page" onClick={() => app.editor.setPage(app.editor.addPage(undefined, app.pageKind()))}>
            {Icons.plus}
          </button>
        </div>
        {pages.map((p) => (
          <PageItem key={p.id} id={p.id} active={p.id === pageId} onChange={() => setTick((t) => t + 1)} />
        ))}
      </div>
      <div className="panel-tabs">
        <button className={tab === 'layers' ? 'on' : ''} onClick={() => setTab('layers')}>
          Layers
        </button>
        <button className={tab === 'assets' ? 'on' : ''} onClick={() => setTab('assets')}>
          Assets
        </button>
      </div>
      {tab === 'layers' ? <LayerTree /> : <AssetsPanel />}
    </div>
  );
}

function PageItem({ id, active }: { id: NodeId; active: boolean; onChange: () => void }) {
  const page = useApp('doc', () => app.doc.get(id));
  const [editing, setEditing] = useState(false);
  if (!page) return null;
  return (
    <div
      className={`page-item ${active ? 'active' : ''}`}
      onClick={() => app.editor.setPage(id)}
      onDoubleClick={() => setEditing(true)}
      onContextMenu={(e) => {
        e.preventDefault();
        if (app.doc.pages().length > 1 && confirm(`Delete page "${page.name}"?`)) app.editor.remove([id]);
      }}
    >
      <span style={{ color: 'var(--text-2)' }}>{page.pageKind === 'WHITEBOARD' ? Icons.board : Icons.page}</span>
      {editing ? (
        <input
          autoFocus
          defaultValue={page.name}
          style={{ border: '1px solid var(--accent)', borderRadius: 2, outline: 'none', flex: 1 }}
          onBlur={(e) => {
            app.editor.update(id, { name: e.target.value || page.name }, 'Rename page');
            setEditing(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            if (e.key === 'Escape') setEditing(false);
            e.stopPropagation();
          }}
        />
      ) : (
        <span>{page.name}</span>
      )}
      <span className="kind">{page.pageKind === 'WHITEBOARD' ? 'Board' : ''}</span>
    </div>
  );
}

function LayerTree() {
  const version = useApp(['doc', 'selection'], () => `${app.docVersion}:${app.editor.selection.join(',')}:${app.editor.pageId}`);
  const scroller = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [height, setHeight] = useState(600);
  // shared with ⌘R and the canvas titles (see App.startRename)
  const renaming = useApp('ui', () => (app.ui.renaming?.on === 'layers' ? app.ui.renaming.id : null));
  const setRenaming = (id: NodeId | null) => app.setUI({ renaming: id ? { id, on: 'layers' } : null });
  const [drop, setDrop] = useState<{ id: NodeId; where: 'before' | 'after' | 'inside' } | null>(null);
  const dragIds = useRef<NodeId[]>([]);
  const anchor = useRef<NodeId | null>(null);
  const [, force] = useState(0);

  // Expand ancestors of the selection so it is visible.
  const selection = app.editor.selection;
  for (const id of selection) for (const a of app.doc.ancestors(id)) if (a.type !== 'PAGE' && a.type !== 'DOCUMENT') expanded.add(a.id);

  const rows = useMemo(() => {
    const out: Row[] = [];
    const visit = (id: NodeId, depth: number) => {
      const kids = app.doc.children(id);
      // top of the list = front-most layer
      for (let i = kids.length - 1; i >= 0; i--) {
        const k = kids[i];
        const hasKids = isContainer(k.type) && app.doc.children(k.id).length > 0;
        out.push({ node: k, depth, hasKids });
        if (hasKids && expanded.has(k.id)) visit(k.id, depth + 1);
      }
    };
    if (app.doc.has(app.editor.pageId)) visit(app.editor.pageId, 0);
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, renaming, drop === null]);

  useEffect(() => {
    const el = scroller.current!;
    const ro = new ResizeObserver(() => setHeight(el.clientHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Keep the first selected row in view.
  useEffect(() => {
    const first = selection[0];
    if (!first) return;
    const i = rows.findIndex((r) => r.node.id === first);
    const el = scroller.current;
    if (i < 0 || !el) return;
    const y = i * ROW;
    if (y < el.scrollTop || y > el.scrollTop + el.clientHeight - ROW) el.scrollTop = Math.max(0, y - el.clientHeight / 2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection.join(',')]);

  const sel = new Set(selection);
  const start = Math.max(0, Math.floor(scrollTop / ROW) - 10);
  const end = Math.min(rows.length, Math.ceil((scrollTop + height) / ROW) + 10);

  const click = (e: React.MouseEvent, id: NodeId) => {
    if (e.shiftKey && anchor.current) {
      const a = rows.findIndex((r) => r.node.id === anchor.current);
      const b = rows.findIndex((r) => r.node.id === id);
      if (a >= 0 && b >= 0) {
        const [lo, hi] = a < b ? [a, b] : [b, a];
        app.editor.setSelection(rows.slice(lo, hi + 1).map((r) => r.node.id));
        return;
      }
    }
    if (e.metaKey || e.ctrlKey) app.editor.setSelection(sel.has(id) ? selection.filter((x) => x !== id) : [...selection, id]);
    else app.editor.setSelection([id]);
    anchor.current = id;
  };

  const onDrop = () => {
    const d = drop;
    setDrop(null);
    if (!d || !dragIds.current.length) return;
    const target = app.doc.get(d.id);
    if (!target) return;
    const ids = dragIds.current.filter((id) => id !== d.id && !app.doc.isAncestor(id, d.id));
    if (!ids.length) return;
    app.editor.transact('Move layers', () => {
      if (d.where === 'inside') {
        for (const id of ids) app.editor.reparent(id, target.id);
        expanded.add(target.id);
      } else {
        const parent = target.parent!;
        const siblings = app.doc.children(parent).filter((k) => !ids.includes(k.id));
        let pos = siblings.findIndex((k) => k.id === target.id);
        // list is reversed (front first): "before" in the list = in front of target = higher index
        if (d.where === 'before') pos += 1;
        for (const id of [...ids].reverse()) {
          if (app.doc.get(id)!.parent !== parent) app.editor.reparent(id, parent, pos);
          else app.editor.setZIndex(id, pos);
        }
      }
    });
  };

  return (
    <div ref={scroller} className="layers" onScroll={(e) => setScrollTop((e.target as HTMLDivElement).scrollTop)} onClick={(e) => e.target === e.currentTarget && app.editor.setSelection([])}>
      <div style={{ height: rows.length * ROW, position: 'relative' }}>
        {rows.slice(start, end).map((r, i) => {
          const n = r.node;
          const idx = start + i;
          const isSel = sel.has(n.id);
          const childOfSel = !isSel && app.doc.ancestors(n.id).some((a) => sel.has(a.id));
          const comp = n.type === 'COMPONENT' || n.type === 'INSTANCE';
          const dropCls = drop?.id === n.id ? `drop-${drop.where}` : '';
          return (
            <div
              key={n.id}
              className={`layer ${isSel ? 'selected' : ''} ${childOfSel ? 'child-of-selected' : ''} ${comp ? 'component' : ''} ${!n.visible ? 'hidden' : ''} ${dropCls}`}
              style={{ position: 'absolute', top: idx * ROW, left: 0, right: 0, paddingLeft: 8 + r.depth * 16 }}
              draggable={renaming !== n.id}
              onDragStart={(e) => {
                dragIds.current = sel.has(n.id) ? selection : [n.id];
                e.dataTransfer.effectAllowed = 'move';
              }}
              onDragOver={(e) => {
                e.preventDefault();
                const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                const y = (e.clientY - rect.top) / rect.height;
                const canNest = isContainer(n.type) && n.type !== 'INSTANCE';
                setDrop({ id: n.id, where: canNest && y > 0.3 && y < 0.7 ? 'inside' : y < 0.5 ? 'before' : 'after' });
              }}
              onDragLeave={() => setDrop(null)}
              onDrop={(e) => {
                e.preventDefault();
                onDrop();
              }}
              onClick={(e) => click(e, n.id)}
              onDoubleClick={() => setRenaming(n.id)}
              onMouseEnter={() => app.setUI({ hoverId: n.id })}
              onMouseLeave={() => app.ui.hoverId === n.id && app.setUI({ hoverId: null })}
            >
              <span
                className="twisty"
                onClick={(e) => {
                  e.stopPropagation();
                  if (expanded.has(n.id)) expanded.delete(n.id);
                  else expanded.add(n.id);
                  force((x) => x + 1);
                  app.emit('doc');
                }}
              >
                {r.hasKids ? (expanded.has(n.id) ? Icons.chevronDown : Icons.chevronRight) : null}
              </span>
              <span className="icon">{nodeIcon(n.type)}</span>
              <span className="name">
                {renaming === n.id ? (
                  <input
                    autoFocus
                    defaultValue={n.name}
                    onFocus={(e) => e.target.select()}
                    onBlur={(e) => {
                      const name = e.target.value.trim();
                      if (!e.target.dataset.cancel && name && name !== n.name) app.editor.update(n.id, { name }, 'Rename');
                      if (app.ui.renaming?.id === n.id) setRenaming(null);
                    }}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                      if (e.key === 'Escape') {
                        (e.target as HTMLInputElement).dataset.cancel = '1';
                        (e.target as HTMLInputElement).blur();
                      }
                    }}
                  />
                ) : (
                  n.name
                )}
              </span>
              <span className={`actions ${!n.visible || n.locked ? 'on' : ''}`}>
                <button
                  title={n.locked ? 'Unlock' : 'Lock'}
                  onClick={(e) => {
                    e.stopPropagation();
                    app.editor.update(n.id, { locked: !n.locked }, 'Toggle lock');
                  }}
                >
                  {n.locked ? Icons.lock : Icons.unlock}
                </button>
                <button
                  title={n.visible ? 'Hide' : 'Show'}
                  onClick={(e) => {
                    e.stopPropagation();
                    app.editor.update(n.id, { visible: !n.visible }, 'Toggle visibility');
                  }}
                >
                  {n.visible ? Icons.eye : Icons.eyeOff}
                </button>
              </span>
            </div>
          );
        })}
      </div>
      {rows.length === 0 && <div className="empty-hint">No layers yet. Pick a tool below, or connect an AI agent (top right) and ask it to build something.</div>}
    </div>
  );
}
